import { ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdmissionSalesDates } from '@components/admission-sales-dates';
import { DatePicker } from '@components/date-picker';
import { IconCross } from '@components/icons';
import { Input } from '@components/input';
import { InputPrice } from '@components/input-price';
import { LabelPrimary } from '@components/label';
import { LabelInfo } from '@components/label/src/label-info';
import { Multiselect } from '@components/multiselect/multiselect';
import { MultiselectOption } from '@components/multiselect/multiselect.type';
import { SectionDisclosure } from '@components/section-disclosure';
import { Textarea } from '@components/textarea';
import { TextareaCounter } from '@components/textarea-counter';
import { ToggleSection } from '@components/toggle-section';
import { TOGGLE_SECTION_BADGE_NEW } from '@components/toggle-section/src/toggle-section.type';
import { TranslationContentMap } from '@components/translation-fields';
import { TranslationSection } from '@components/translation-section';
import { SupportedLanguages } from '@constants/constants.type';
import { LANGAUAGE_PRIORITY, WEEK_DAYS } from '@constants/global';
import { Translations } from '@domain/admission';
import { formatCurrency } from '@utils/price-formatter/price-formatter';
import classNames from 'classnames';
import { Moment } from 'moment';

import {
  BasicInfoErrors,
  BasicInfoSectionProps,
  BasicInfoValue,
  ExtraGuestsValue,
  PassValidityValue,
  StartAfterValue,
} from './basic-info-section.type';

export const BASIC_INFO_SECTION_TEST_ID = 'basic-info-section';
export const BASIC_INFO_SECTION_TITLE_MAX_LENGTH = 100;
export const BASIC_INFO_SECTION_DESCRIPTION_MAX_LENGTH = 350;

export const passValidityEndShouldClear = (newStart: Moment | null, currentEnd: Moment | null): boolean =>
  !!newStart && !!currentEnd && currentEnd.isBefore(newStart);

const testId = (prefix: string, suffix: string) => `${prefix}-${suffix}`;

const FieldGroup = ({
  prefix,
  testid,
  label,
  tooltip,
  reserveLabel,
  error,
  children,
  className,
}: {
  prefix: string;
  testid: string;
  label: string;
  tooltip?: string;
  reserveLabel?: boolean;
  error?: string;
  children: ReactNode | ((fieldId: string) => ReactNode);
  className?: string;
}) => {
  const fieldId = useId();
  const associatesLabel = typeof children === 'function';
  return (
    <div className={classNames('flex flex-col gap-2 flex-1 min-w-0', className)} data-testid={testId(prefix, testid)}>
      {/* reserveLabel pads the label to 2 lines at lg (the first row's narrow columns wrap there); xl releases it. */}
      <div className={reserveLabel ? 'lg:min-h-8 xl:min-h-0' : undefined}>
        {tooltip ? (
          <LabelInfo
            htmlFor={associatesLabel ? fieldId : undefined}
            text={tooltip}
            ariaLabel={label}
            marginBottomClassName=''
            customColors='text-secondary'>
            {label}
          </LabelInfo>
        ) : (
          <LabelPrimary
            htmlFor={associatesLabel ? fieldId : undefined}
            marginBottomClassName=''
            customColors='text-secondary'>
            {label}
          </LabelPrimary>
        )}
      </div>
      <div className='flex flex-col'>{associatesLabel ? children(fieldId) : children}</div>
      {error && (
        <span data-testid={testId(prefix, `${testid}-error`)} className='text-body-xs text-critical-no-bg'>
          {error}
        </span>
      )}
    </div>
  );
};

const ExtraGuestsField = ({
  prefix,
  value,
  onChange,
  disabled,
  currency,
  errors,
}: {
  prefix: string;
  value: ExtraGuestsValue;
  onChange: (next: Partial<ExtraGuestsValue>) => void;
  disabled: boolean;
  currency: string;
  errors?: BasicInfoErrors;
}) => {
  const { t } = useTranslation();
  const [maxQtyTouched, setMaxQtyTouched] = useState(false);
  const [priceTouched, setPriceTouched] = useState(false);
  const maxQtyError = maxQtyTouched ? errors?.extraGuestsMaxQty : undefined;
  const priceError = priceTouched ? errors?.extraGuestsPricePerGuest : undefined;

  return (
    <ToggleSection
      value={value.enabled}
      onChange={(next) => onChange({ enabled: next })}
      label={t('offers:admissionBasicInfoExtraGuestsLabel')}
      subLabel={t('offers:admissionBasicInfoExtraGuestsSublabel')}
      badge={TOGGLE_SECTION_BADGE_NEW}
      disabled={disabled}
      dataTestId={testId(prefix, 'extra-guests')}>
      <div className='flex items-start gap-4 w-full pt-2'>
        <FieldGroup
          prefix={prefix}
          testid='extra-guests-max-qty'
          label={t('offers:admissionBasicInfoExtraGuestsMaxQtyLabel')}
          error={maxQtyError}>
          {(id) => (
            <Input
              id={id}
              type='number'
              value={value.maxQty}
              onChange={(e) => {
                setMaxQtyTouched(true);
                onChange({ maxQty: e.currentTarget.value });
              }}
              disabled={disabled}
              hasError={Boolean(maxQtyError)}
              placeholder='-'
            />
          )}
        </FieldGroup>
        <FieldGroup
          prefix={prefix}
          testid='extra-guests-price'
          label={t('offers:admissionBasicInfoExtraGuestsPricePerGuestLabel')}
          error={priceError}>
          {(id) => (
            <InputPrice
              id={id}
              value={value.pricePerGuest}
              currency={currency}
              onChange={(e) => {
                setPriceTouched(true);
                onChange({ pricePerGuest: e.currentTarget.value });
              }}
              disabled={disabled}
              hasError={Boolean(priceError)}
              placeholder='-'
            />
          )}
        </FieldGroup>
      </div>
    </ToggleSection>
  );
};

const PassValidityField = ({
  prefix,
  value,
  onChange,
  disabled,
  timezone,
  locale,
  today,
  errors,
  startAfter,
  onOpenStartAfter,
  onStartAfterClear,
}: {
  prefix: string;
  value: PassValidityValue;
  onChange: (next: Partial<PassValidityValue>) => void;
  disabled: boolean;
  timezone: string;
  locale: string;
  today: Moment;
  errors?: BasicInfoErrors;
  startAfter?: StartAfterValue;
  onOpenStartAfter?: () => void;
  onStartAfterClear?: () => void;
}) => {
  const { t } = useTranslation();

  const dayOptions: MultiselectOption[] = WEEK_DAYS.map((day) => ({
    id: day.id,
    name: t(day.locale),
    checked: value.days.includes(day.id),
  }));

  const handleDayChange = (id: string, checked: boolean) => {
    const day = WEEK_DAYS.find((w) => w.id === id)?.id;
    if (!day) return;
    const selected = new Set(value.days);
    if (checked) selected.add(day);
    else selected.delete(day);
    const nextDays = WEEK_DAYS.filter((w) => selected.has(w.id)).map((w) => w.id);
    onChange({ days: nextDays });
  };

  const handleSelectAllDays = (selectAll: boolean) => {
    onChange({ days: selectAll ? WEEK_DAYS.map((d) => d.id) : [] });
  };

  return (
    <div
      className={classNames('grid grid-cols-2 gap-4 w-full', startAfter ? 'lg:grid-cols-4' : 'lg:grid-cols-3')}
      data-testid={testId(prefix, 'pass-validity')}>
      <FieldGroup
        prefix={prefix}
        testid='pass-validity-start'
        label={t('passes:passFromValidityLabel')}
        error={errors?.passValidityStart}>
        {(id) => (
          <DatePicker
            id={id}
            value={value.start}
            onChange={(date) => {
              if (passValidityEndShouldClear(date, value.end)) {
                onChange({ start: date, end: null });
              } else {
                onChange({ start: date });
              }
            }}
            disabled={disabled}
            readOnly={disabled}
            hasError={Boolean(errors?.passValidityStart)}
            placeholder={t('common:dateLabel')}
            timezone={timezone}
            locale={locale}
            minBookingDate={today}
            initialVisibleMonth={value.start ? () => value.start as Moment : null}
            data-testid={testId(prefix, 'pass-validity-start-input')}
          />
        )}
      </FieldGroup>
      <FieldGroup
        prefix={prefix}
        testid='pass-validity-end'
        label={t('passes:passUntilValidityLabel')}
        error={errors?.passValidityEnd}>
        {(id) => (
          <DatePicker
            id={id}
            value={value.end}
            onChange={(date) => onChange({ end: date })}
            disabled={disabled}
            readOnly={disabled}
            hasError={Boolean(errors?.passValidityEnd)}
            placeholder={t('common:dateLabel')}
            timezone={timezone}
            locale={locale}
            minBookingDate={value.start ?? today}
            initialVisibleMonth={value.end ? () => value.end as Moment : null}
            data-testid={testId(prefix, 'pass-validity-end-input')}
          />
        )}
      </FieldGroup>
      <FieldGroup
        prefix={prefix}
        testid='pass-validity-days'
        label={t('passes:validityDaysLabel')}
        error={errors?.passValidityDays}>
        {(id) => (
          <Multiselect
            id={id}
            wrapperClassName=''
            menuClassName='left-0 min-w-full w-max max-w-[min(20rem,calc(100vw-2rem))]'
            options={dayOptions}
            onChange={handleDayChange}
            handleSelectedAll={handleSelectAllDays}
            disabled={disabled}
            hasError={Boolean(errors?.passValidityDays)}
            translations={{
              deselectAll: t('offers:deselectAllLabel'),
              selectAll: t('offers:selectAllLabel'),
              noOptionsSelected: t('passes:passNoDaysSelected'),
            }}
          />
        )}
      </FieldGroup>
      {startAfter !== undefined && (
        <FieldGroup prefix={prefix} testid='pass-start-after' label={t('passes:startAfter')}>
          <div className='relative'>
            <button
              type='button'
              data-testid={testId(prefix, 'pass-start-after-button')}
              onClick={onOpenStartAfter}
              disabled={disabled}
              className='flex h-12 w-full items-center rounded-lg border border-extra-soft bg-transparent pl-4 pr-10 text-left text-body-m disabled:opacity-50'>
              <span className={classNames('truncate', { 'text-secondary': !startAfter.passId })}>
                {startAfter.passId ? startAfter.displayName : t('passes:selectAPass')}
              </span>
            </button>
            {startAfter.passId && !disabled && (
              <button
                type='button'
                aria-label='Clear start-after pass'
                data-testid={testId(prefix, 'pass-start-after-clear')}
                onClick={onStartAfterClear}
                className='absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full hover:bg-extra-soft'>
                <IconCross className='h-3 w-3 text-secondary' variant='s' />
              </button>
            )}
          </div>
        </FieldGroup>
      )}
    </div>
  );
};

const buildTranslationContentMap = (title: Translations, description: Translations): TranslationContentMap => {
  const out: TranslationContentMap = {};
  for (const locale of LANGAUAGE_PRIORITY) {
    out[locale] = {
      title: title[locale] ?? '',
      description: description[locale] ?? '',
    };
  }
  return out;
};

export const BasicInfoSection = ({
  value,
  onChange,
  disabled = false,
  lockPricing = false,
  lockDownpayment = false,
  lockFields = false,
  currency,
  mainLanguage,
  priceTooltip,
  errors,
  translationsLoading = false,
  salesWindowContext,
  salesWindowVariant,
  onOpenStartAfter,
  open,
  onToggleOpen,
  showTopBorder = true,
  maxItemsPlaceholder = '5',
  dataTestId = BASIC_INFO_SECTION_TEST_ID,
}: BasicInfoSectionProps) => {
  const { t } = useTranslation();
  const fieldsDisabled = disabled || lockFields;
  const pricingDisabled = fieldsDisabled || lockPricing;
  const downpaymentDisabled = fieldsDisabled || lockDownpayment;

  const update = <K extends keyof BasicInfoValue>(key: K, next: BasicInfoValue[K]) => {
    onChange({ [key]: next });
  };

  const updateTranslation = (key: 'title' | 'description', text: string) => {
    onChange({ [key]: { ...value[key], [mainLanguage]: text } });
  };

  const handleTranslationContentChange = (next: TranslationContentMap) => {
    const nextTitle: Translations = { ...value.title };
    const nextDescription: Translations = { ...value.description };
    (Object.entries(next) as [SupportedLanguages, { title: string; description: string }][]).forEach(
      ([locale, content]) => {
        nextTitle[locale] = content.title === '' && value.title[locale] === null ? null : content.title;
        nextDescription[locale] =
          content.description === '' && value.description[locale] === null ? null : content.description;
      }
    );
    onChange({ title: nextTitle, description: nextDescription });
  };

  const titleText = value.title[mainLanguage] ?? '';
  const descriptionText = value.description[mainLanguage] ?? '';
  const salesWindow = value.salesWindow;
  const reserveFirstRowLabels = value.ddp === undefined;

  const priceField = (
    <FieldGroup
      prefix={dataTestId}
      testid='price'
      label={t('offers:admissionPriceLabel')}
      tooltip={priceTooltip}
      reserveLabel={reserveFirstRowLabels}
      error={errors?.price}>
      {(id) => (
        <InputPrice
          id={id}
          value={value.price}
          currency={currency}
          onChange={(e) => update('price', e.currentTarget.value)}
          disabled={pricingDisabled}
          hasError={Boolean(errors?.price)}
          placeholder='-'
        />
      )}
    </FieldGroup>
  );

  const ddpField =
    value.ddp === undefined ? null : (
      <FieldGroup prefix={dataTestId} testid='ddp' label={t('offers:admissionDDPLabel')} error={errors?.ddp}>
        {(id) => (
          <Input
            id={id}
            type='number'
            value={value.ddp}
            onChange={(e) => update('ddp', e.currentTarget.value)}
            disabled={pricingDisabled}
            hasError={Boolean(errors?.ddp)}
            placeholder='-'
          />
        )}
      </FieldGroup>
    );

  const quantityField = (
    <FieldGroup
      prefix={dataTestId}
      testid='quantity'
      label={t('offers:admissionQuantityLabel')}
      reserveLabel={reserveFirstRowLabels}
      error={errors?.quantity}>
      {(id) => (
        <Input
          id={id}
          type='number'
          value={value.quantity}
          onChange={(e) => update('quantity', e.currentTarget.value)}
          disabled={fieldsDisabled}
          hasError={Boolean(errors?.quantity)}
          placeholder='∞'
        />
      )}
    </FieldGroup>
  );

  const minItemsField = (
    <FieldGroup
      prefix={dataTestId}
      testid='items-min'
      label={t('offers:admissionMinItemsPerTransactionLabel')}
      reserveLabel={reserveFirstRowLabels}
      error={errors?.itemsPerTransactionMin}>
      {(id) => (
        <Input
          id={id}
          type='number'
          value={value.itemsPerTransaction.min}
          onChange={(e) => update('itemsPerTransaction', { ...value.itemsPerTransaction, min: e.currentTarget.value })}
          disabled={fieldsDisabled}
          hasError={Boolean(errors?.itemsPerTransactionMin)}
          placeholder='1'
        />
      )}
    </FieldGroup>
  );

  const maxItemsField = (
    <FieldGroup
      prefix={dataTestId}
      testid='items-max'
      label={t('offers:admissionMaxItemsPerTransactionLabel')}
      reserveLabel={reserveFirstRowLabels}
      error={errors?.itemsPerTransactionMax}>
      {(id) => (
        <Input
          id={id}
          type='number'
          value={value.itemsPerTransaction.max}
          onChange={(e) => update('itemsPerTransaction', { ...value.itemsPerTransaction, max: e.currentTarget.value })}
          disabled={fieldsDisabled}
          hasError={Boolean(errors?.itemsPerTransactionMax)}
          placeholder={maxItemsPlaceholder}
        />
      )}
    </FieldGroup>
  );

  const sectionBody = (
    <section data-testid={dataTestId} className='flex flex-col gap-4 w-full py-4 md:py-6'>
      {ddpField ? (
        <>
          <div className='flex items-start gap-4 w-full'>
            {priceField}
            {ddpField}
            {quantityField}
          </div>
          <div className='flex items-start gap-4 w-full'>
            {minItemsField}
            {maxItemsField}
          </div>
        </>
      ) : (
        <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
          {priceField}
          {quantityField}
          {minItemsField}
          {maxItemsField}
        </div>
      )}

      {value.downpayment !== undefined && (
        <FieldGroup
          prefix={dataTestId}
          testid='downpayment'
          label={t('offers:downpayment')}
          error={errors?.downpayment}>
          {(id) => (
            <>
              <div className='w-full md:w-1/2'>
                <InputPrice
                  id={id}
                  value={value.downpayment}
                  currency={currency}
                  onChange={(e) => update('downpayment', e.currentTarget.value)}
                  disabled={downpaymentDisabled}
                  hasError={Boolean(errors?.downpayment)}
                  placeholder='-'
                />
              </div>
              <p
                data-testid={testId(dataTestId, 'total-price-annotation')}
                className='pt-2 text-body-xs text-secondary'>
                <span className='font-semibold'>
                  {t('offers:totalPrice')}: {formatCurrency({ currency, number: Number(value.price) })}
                </span>{' '}
                {`(${formatCurrency({ currency, number: Number(value.downpayment) })} ${t(
                  'offers:downpayment'
                ).toLocaleLowerCase()} + ${formatCurrency({
                  currency,
                  number: Number(value.price) - Number(value.downpayment),
                })} ${t('offers:onPremise')})`}
              </p>
            </>
          )}
        </FieldGroup>
      )}

      {salesWindow && (
        <AdmissionSalesDates
          value={salesWindow}
          onChange={(next) => update('salesWindow', { ...salesWindow, ...next })}
          context={salesWindowContext}
          disabled={disabled}
          variant={salesWindowVariant}
          errors={{ salesStart: errors?.salesStart, salesEnd: errors?.salesEnd }}
          dataTestId={testId(dataTestId, 'sales-window')}
        />
      )}

      {value.passValidity !== undefined && (
        <PassValidityField
          prefix={dataTestId}
          value={value.passValidity}
          onChange={(next) => update('passValidity', { ...(value.passValidity as PassValidityValue), ...next })}
          disabled={fieldsDisabled}
          timezone={salesWindowContext.timezone}
          locale={salesWindowContext.locale}
          today={salesWindowContext.now}
          errors={errors}
          startAfter={value.startAfter}
          onOpenStartAfter={onOpenStartAfter}
          onStartAfterClear={() => update('startAfter', { passId: null, displayName: '' })}
        />
      )}

      {value.guestsPerAdmission !== undefined && (
        <FieldGroup
          prefix={dataTestId}
          testid='guests-per-admission'
          label={t('offers:admissionGuestCapacityLabel')}
          tooltip={t('offers:admissionGuestCapacityTooltip')}
          error={errors?.guestsPerAdmission}>
          {(id) => (
            <div className='w-full md:w-1/2'>
              <Input
                id={id}
                type='number'
                value={value.guestsPerAdmission}
                onChange={(e) => update('guestsPerAdmission', e.currentTarget.value)}
                disabled={fieldsDisabled}
                hasError={Boolean(errors?.guestsPerAdmission)}
                placeholder='-'
              />
            </div>
          )}
        </FieldGroup>
      )}

      {value.extraGuests !== undefined && (
        <ExtraGuestsField
          prefix={dataTestId}
          value={value.extraGuests}
          onChange={(next) => update('extraGuests', { ...(value.extraGuests as ExtraGuestsValue), ...next })}
          disabled={fieldsDisabled}
          currency={currency}
          errors={errors}
        />
      )}

      <FieldGroup
        prefix={dataTestId}
        testid='title'
        label={t('offers:admissionBasicInfoTitleLabel')}
        error={errors?.title}>
        {(id) => (
          <Textarea
            id={id}
            value={titleText}
            onChange={(e) => updateTranslation('title', e.currentTarget.value)}
            disabled={fieldsDisabled}
            hasError={Boolean(errors?.title)}
            placeholder={t('offers:admissionBasicInfoTitlePlaceholder')}
            maxLength={BASIC_INFO_SECTION_TITLE_MAX_LENGTH}
            className='h-24 md:h-[74px] resize-none'
          />
        )}
      </FieldGroup>

      <FieldGroup
        prefix={dataTestId}
        testid='description'
        label={t('offers:admissionOfferDescriptionLabel')}
        error={errors?.description}>
        {(id) => (
          <TextareaCounter
            id={id}
            value={descriptionText}
            onChange={(e) => updateTranslation('description', e.currentTarget.value)}
            disabled={fieldsDisabled}
            hasError={Boolean(errors?.description)}
            placeholder={t('offers:admissionOfferDescriptionPlaceholder')}
            length={descriptionText.length}
            maxLength={BASIC_INFO_SECTION_DESCRIPTION_MAX_LENGTH}
            charactersText=''
            rows={5}
          />
        )}
      </FieldGroup>

      <div data-testid={testId(dataTestId, 'translations-toggle')}>
        <TranslationSection
          enabled={value.translationsEnabled}
          onEnabledChange={(next) => update('translationsEnabled', next)}
          value={buildTranslationContentMap(value.title, value.description)}
          onChange={handleTranslationContentChange}
          mainLanguage={mainLanguage}
          loading={translationsLoading}
          disabled={fieldsDisabled}
          titleLabel={t('offers:admissionBasicInfoTitleLabel')}
          descriptionLabel={t('offers:admissionOfferDescriptionLabel')}
          titleMaxLength={BASIC_INFO_SECTION_TITLE_MAX_LENGTH}
          descriptionMaxLength={BASIC_INFO_SECTION_DESCRIPTION_MAX_LENGTH}
          titleAsTextarea
        />
      </div>
    </section>
  );

  return (
    <SectionDisclosure
      title={t('offers:admissionBasicInfoSectionTitle')}
      open={open}
      onToggleOpen={onToggleOpen}
      showTopBorder={showTopBorder}
      dataTestId={`${dataTestId}-disclosure`}>
      {sectionBody}
    </SectionDisclosure>
  );
};
